import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, GitBranch, FolderGit2 } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatCost, formatDuration, formatNumber } from '../lib/format'
import { realtime } from '../lib/realtime'
import { Avatar, Badge, EmptyState, LiveDot, ProgressBar, Skeleton, toneForState } from '../components/primitives'
import type { LiveSession } from '../lib/types'
import { branchSplits, type BranchPeer } from '../lib/branches'

/**
 * The engineering floor.
 *
 * One card per active session, blocked work first. This is the "what engineering work is happening
 * right now" view; the Bug Hunt answers a narrower question with the same data, and neither
 * replaces the other.
 */
/** The Floor's report view. With a project chosen on the Floor, only that project's sessions are listed. */
export default function Live({ projectId }: { projectId?: string } = {}) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const [pulse, setPulse] = useState(0)

  const { data, isLoading } = useQuery({ queryKey: ['live'], queryFn: () => api.dashboard.live() })

  useEffect(
    () =>
      realtime.onSessions((updates) => {
        queryClient.setQueryData<LiveSession[]>(['live'], (current) => {
          const byId = new Map((current ?? []).map((s) => [s.workSessionId, s]))
          for (const update of updates) byId.set(update.workSessionId, update)

          return [...byId.values()]
            .filter((s) => !s.isTerminal || Date.now() - new Date(s.lastActivityAt).getTime() < 5 * 60_000)
            .sort((a, b) => {
              // Blocked first: it is the state that needs a person.
              const aBlocked = a.activityState === 'Blocked' ? 0 : 1
              const bBlocked = b.activityState === 'Blocked' ? 0 : 1
              if (aBlocked !== bBlocked) return aBlocked - bBlocked
              return b.lastActivityAt.localeCompare(a.lastActivityAt)
            })
        })
      }),
    [queryClient],
  )

  // Durations tick locally once a second. Asking the server for an elapsed time that the client can
  // compute from a start timestamp would be a request per second per card, for no new information.
  useEffect(() => {
    const timer = setInterval(() => setPulse((n) => n + 1), 1000)
    return () => clearInterval(timer)
  }, [])

  const sessions = useMemo(() => (data ?? []).filter((s) => !projectId || s.projectId === projectId), [data, projectId])
  // Agents in the same repository on different branches: said on each card, before their work collides.
  const splits = useMemo(() => branchSplits(sessions), [sessions])

  if (isLoading) {
    return (
      <div className="grid grid--cards">
        {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} height={220} />)}
      </div>
    )
  }

  if (sessions.length === 0) {
    return (
      <EmptyState
        title={t('live.empty')}
        body={t('live.emptyHint')}
        action={<Link to="/onboarding" className="btn btn--primary">{t('nav.onboarding')}</Link>}
      />
    )
  }

  return (
    <div className="stack">
      <div className="row row--between">
        <p className="secondary">{t('live.subtitle')}</p>
        <span className="row row--tight tiny muted">
          <LiveDot state="live" />
          {formatNumber(sessions.filter((s) => !s.isTerminal).length, 'en-US')} {t('common.live').toLowerCase()}
        </span>
      </div>

      <div className="grid grid--cards">
        {sessions.map((session) => (
          <DeveloperCard key={session.workSessionId} session={session} tick={pulse} others={splits.get(session.workSessionId) ?? []} />
        ))}
      </div>
    </div>
  )
}

function DeveloperCard({ session, tick, others }: { session: LiveSession; tick: number; others: BranchPeer[] }) {
  const { t, locale } = useI18n()

  // Recomputed from the start timestamp on every tick, so a card that receives no events still
  // shows time passing rather than appearing frozen.
  void tick
  const elapsed = session.isTerminal
    ? session.durationSeconds
    : Math.max(session.durationSeconds, Math.floor((Date.now() - new Date(session.startedAt).getTime()) / 1000))

  const tone = toneForState(session.activityState)

  return (
    <Link
      to={`/work/${session.workSessionId}`}
      className="panel"
      style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
    >
      <div className="panel__header" style={{ gap: 'var(--s3)' }}>
        <Avatar name={session.developerName} color={session.avatarColor} />
        <div className="stack" style={{ gap: 0, minWidth: 0, flex: 1 }}>
          <span className="bold truncate">{session.developerName}</span>
          {session.taskExternalId && <span className="mono tiny muted nowrap">{session.taskExternalId}</span>}
        </div>
        {/*
          The short form here. A card header is four elements wide and "DADOS DE DEMONSTRAÇÃO" is
          twenty-two characters; at that length it pushed the task id under the avatar and clipped
          the status badge off the right edge. The full wording is on the page header, where there
          is room for it.
        */}
        {session.isSimulated && <Badge tone="demo">{t('common.demoDataShort')}</Badge>}
        <Badge tone={tone}>{t(`state.${session.activityState}`)}</Badge>
      </div>

      <div className="panel__body stack">
        {session.taskTitle && <div className="truncate">{session.taskTitle}</div>}

        <ProgressBar
          value={session.progressPercent}
          showValue
          size="lg"
          tone={session.activityState === 'Blocked' ? 'danger' : session.activityState === 'Completed' ? 'ok' : undefined}
          label={t('work.progress')}
        />

        {(session.currentStepNote ?? session.currentStepTitle) && (
          <div className="stack" style={{ gap: 2 }}>
            <span className="tiny muted">{t('live.currentStep')}</span>
            <span className="small truncate">{session.currentStepNote ?? session.currentStepTitle}</span>
          </div>
        )}

        <div className="row row--wrap row--tight tiny muted">
          {(session.projectName || session.projectKey) && (
            <span className="row row--tight" title={t('projectPicker.label')}>
              <strong>{session.projectName ?? session.projectKey}</strong>
            </span>
          )}
          {session.repositoryName && (
            <span className="row row--tight">
              <FolderGit2 size={11} />
              <span className="mono">{session.repositoryName}</span>
            </span>
          )}
          {/* With a repository, the branch is always shown — "?" when the agent did not report it. */}
          {(session.branch || session.repositoryName) && (
            <span className="row row--tight" title={t('branch.agentOn')}>
              <GitBranch size={11} />
              <span className="mono truncate" style={{ maxWidth: 160 }}>{session.branch ?? t('branch.unknown')}</span>
            </span>
          )}
        </div>

        {others.length > 0 && (
          <div className="branch-split tiny" role="note">
            <AlertTriangle size={12} />
            <span>
              {t('branch.split')}{' '}
              {others.map((o) => `${o.developerName} → ${o.branch}`).join(' · ')}
            </span>
          </div>
        )}

        <div className="row row--between" style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--s3)' }}>
          <Metric label={t('live.filesChanged')} value={formatNumber(session.filesChanged, locale)} />
          <Metric
            label={t('live.tests')}
            value={session.testsTotal > 0 ? `${session.testsPassed}/${session.testsTotal}` : '—'}
            tone={session.testsTotal > 0 && session.testsPassed < session.testsTotal ? 'warn' : undefined}
          />
          <Metric
            label={t('live.cost')}
            value={session.costAccuracy === 'Unavailable' ? '—' : formatCost(session.costUsd, locale)}
            title={t(`cost.${session.costAccuracy === 'Unavailable' ? 'unavailableHint' : session.costAccuracy === 'Estimated' ? 'estimatedHint' : 'actualHint'}`)}
          />
          <Metric label={t('live.duration')} value={formatDuration(elapsed, locale)} />
        </div>

        <div className="row row--between tiny muted">
          <span>{session.providerName ?? '—'}</span>
          <span>{session.modelName ?? ''}</span>
        </div>
      </div>
    </Link>
  )
}

function Metric({ label, value, tone, title }: { label: string; value: string; tone?: 'warn'; title?: string }) {
  return (
    <div className="stack" style={{ gap: 0 }} title={title}>
      <span className="tiny muted">{label}</span>
      <span className={`small bold${tone === 'warn' ? ' stat__warn' : ''}`}>{value}</span>
    </div>
  )
}
